import { MapItemSUP } from "@/types/search";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface InteractionState {
	hoveredMarkerId: string | null;
	selectedMarkers: Record<string, MapItemSUP>;
	selectedCardId: string | null;
	view: "map" | "list";
	currentCarouselIndex: number;

	// methods
	setHoveredMarkerId: (id: string | null) => void;
	setSelectedMarkers: (markers: Record<string, MapItemSUP>) => void;
	addSelectedMarker: (id: string, data: MapItemSUP) => void;
	removeSelectedMarker: (id: string) => void;
	toggleSelectedMarkers: (id: string, data: MapItemSUP) => void;
	clearSelectedMarkers: () => void;
	setSelectedCardId: (id: string | null) => void;
	setView: (view: "map" | "list") => void;
	setCurrentCarouselIndex: (index: number) => void;
}

export const useInteractionStore = create<InteractionState>()(
	devtools(
		subscribeWithSelector(
			immer(set => ({
				hoveredMarkerId: null,
				selectedMarkers: Object.create(null) as Record<string, MapItemSUP>,
				selectedCardId: null,
				view: "map" as const,
				currentCarouselIndex: 0,
				setHoveredMarkerId: id =>
					set(state => {
						state.hoveredMarkerId = id;
					}),
				setSelectedMarkers: markers =>
					set(state => {
						// Only update if actually changed to prevent unnecessary re-renders
						if (state.selectedMarkers !== markers) {
							state.selectedMarkers = markers;
						}
					}),
				addSelectedMarker: (id, data) =>
					set(state => {
						state.selectedMarkers[id] = data;
					}),
				removeSelectedMarker: id =>
					set(state => {
						delete state.selectedMarkers[id];
					}),
				toggleSelectedMarkers: (id, data) =>
					set(state => {
						if (id in state.selectedMarkers) {
							delete state.selectedMarkers[id];
						} else {
							state.selectedMarkers[id] = data;
						}
					}),
				clearSelectedMarkers: () =>
					set(state => {
						// Only clear if there are markers to clear
						if (Object.keys(state.selectedMarkers).length > 0) {
							state.selectedMarkers = Object.create(null);
						}
					}),
				setSelectedCardId: id =>
					set(state => {
						state.selectedCardId = id;
					}),
				setView: view =>
					set(state => {
						state.view = view;
					}),
				setCurrentCarouselIndex: index =>
					set(state => {
						state.currentCarouselIndex = index;
					}),
			})),
		),
		{ name: "searchPage-interaction-store" },
	),
);
